import { Link } from "@tanstack/react-router";
import { ArrowLeft, Bell, Clock, ShieldCheck } from "lucide-react";
import * as React from "react";

import type { Deal } from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { InlineAlert } from "@/components/ui/inline-alert";
import { StepCircle } from "@/components/ui/step-circle";
import { FormError } from "@/components/ui/form-error";
import { dealToasts, useToast } from "@/components/ui/toast";
import { cn } from "@/lib/utils";

import { stepForField, type DealFieldStep } from "./deal422";
import { DealAssigneeSelector } from "./DealAssigneeSelector";
import { DealConfirmation } from "./DealConfirmation";
import { DealWizardStep1, type DealIdentityValues } from "./DealWizardStep1";
import { DealWizardStep2 } from "./DealWizardStep2";
import { DealWizardStep3 } from "./DealWizardStep3";

/** CHP-202 register-on-behalf: the partner BDM/PO who will own the deal. */
export interface DealWizardTargetOwner {
  /** users.id — sent as target_bdm_id on the draft create. */
  id: string;
  name: string | null;
  /** The owner's organisation — seeds the step-1 partner-org default. */
  orgName: string | null;
}

interface Props {
  /** When resuming a draft, pass the fetched Deal to skip ahead. */
  initialDeal?: Deal;
  /** When a CM/RM/RA registers on behalf of a partner, the selected owner. */
  targetOwner?: DealWizardTargetOwner;
  /** CM/RA/RM on-behalf entry (the manager route). Picks the assignee inline
   * on the first step. Passed explicitly by the route — the wizard can't
   * infer it from the async `useCurrentUser` at mount. */
  onBehalf?: boolean;
  /** CHP-1014: leave the wizard from Step 1 (Client details). Clicking Cancel
   * opens a "Discard this deal registration?" confirm; on discard the route's
   * callback navigates back to its deals dashboard. Omitted (e.g. the manager
   * on-behalf route) → no Cancel button. Any saved draft is kept, not deleted. */
  onCancel?: () => void;
  /** CHP-1033: wizard-wide "Back to deals" header link. The mounting route
   * supplies its own deals dashboard (partner vs manager). Navigating away
   * loses nothing — any saved draft stays resumable. Omitted → no link. */
  backTo?: "/dashboard/partner/deals" | "/dashboard/manager/deals";
}

// CHP-339: the wizard is a uniform three-step flow for every role. The old
// partner-only "Secure Your Opportunity" identity step (which captured the
// partner org + end-customer domain) was removed — the org name now seeds
// from the registering partner's profile (or the chosen on-behalf owner's
// organisation), and the optional end-customer domain is no longer collected
// at registration time. On-behalf (CM/RA/RM) already skipped that step; now
// PO/BDM do too, so the step count is the same for everyone.
//
// CHP-368: a CM/RA/RM registering inline on a partner's behalf gets an extra
// "Assign Deal Owner" FIRST step (the picker, separated out of Client details);
// they can't proceed until they've chosen a certified BDM/PO. Every other entry
// (partner self-registration, or a manager who pre-selected the owner) keeps the
// three-step flow.
type WizardStepKind = "assign" | "client" | "opportunity" | "review";

const BASE_STEPS: { kind: WizardStepKind; label: string }[] = [
  { kind: "client", label: "Client details" },
  { kind: "opportunity", label: "Opportunity" },
  { kind: "review", label: "Review & submit" },
];
const ASSIGN_STEP: { kind: WizardStepKind; label: string } = {
  kind: "assign",
  label: "Assign Deal Owner",
};

function resolveInitialStep(deal: Deal | undefined, lastStep: number): number {
  if (!deal) {
    return 1;
  }
  // last_completed_stage is server data-depth (2=client details filled,
  // 3=opportunity filled; 1=name-only). Resume on the step matching the
  // deepest saved stage: stage 2 → opportunity (step 2), stage 3 → review
  // (step 3). Clamp into [1, lastStep]. (Resume only happens on the
  // three-step flow — the manager inline on-behalf path has no initial draft.)
  const stage = deal.last_completed_stage ?? 1;
  return Math.min(Math.max(stage, 1), lastStep);
}

export function DealRegistrationWizard({
  initialDeal,
  targetOwner,
  onBehalf,
  onCancel,
  backTo,
}: Props) {
  const { accessToken, data: me } = useCurrentUser();
  const { toast } = useToast();
  // Managers registering on-behalf (no pre-selected owner, fresh deal) pick
  // the assignee on a dedicated first step (CHP-368).
  const managerOnBehalf = !!onBehalf && !initialDeal && !targetOwner;
  const steps = managerOnBehalf ? [ASSIGN_STEP, ...BASE_STEPS] : BASE_STEPS;
  const LAST_STEP = steps.length;
  // The on-behalf owner the manager picks on the Assign-Deal-Owner step; wired
  // into the draft's target_bdm_id when Client details creates it. The name is
  // kept alongside so the review step can show who the deal is registered to
  // (CHP-809 Partner & Owner Identity block).
  const [pickedOwnerId, setPickedOwnerId] = React.useState<string | null>(null);
  const [pickedOwnerName, setPickedOwnerName] = React.useState<string | null>(null);
  const [assignError, setAssignError] = React.useState<string | null>(null);
  const [step, setStep] = React.useState(() => resolveInitialStep(initialDeal, LAST_STEP));
  const [dealId, setDealId] = React.useState<string | null>(initialDeal?.id ?? null);
  const [deal, setDeal] = React.useState<Deal | null>(initialDeal ?? null);
  const [submitted, setSubmitted] = React.useState(false);
  // CHP-474: when a later step's submit 422s on an earlier step's field, the
  // wizard jumps to that step and seeds it with the offending field so it
  // highlights + scrolls into view on mount. Keyed by step kind because the
  // step index shifts when the on-behalf Assign step is present.
  const [crossStepError, setCrossStepError] = React.useState<{
    kind: DealFieldStep;
    field: string;
  } | null>(null);
  // CHP-1014: the Step-1 Cancel confirm ("Discard this deal registration?").
  const [confirmCancel, setConfirmCancel] = React.useState(false);

  const effectiveTargetBdmId = targetOwner?.id ?? pickedOwnerId ?? undefined;

  // On-behalf: the org default comes from the selected owner, not the actor.
  // For the manager picker flow (no pre-selected owner) leave it blank — the
  // partner org follows the chosen assignee's profile server-side rather than
  // the manager's own organisation.
  const defaultOrgName = targetOwner
    ? (targetOwner.orgName ?? "")
    : managerOnBehalf
      ? ""
      : (me?.organization?.company_name ?? me?.company_name ?? "");
  // CHP-339: the identity step is gone, so the partner org seeds from the
  // draft (resume) or the profile-derived default, and the end-customer
  // domain (no longer collected) carries through from an existing draft only.
  const identityValues: DealIdentityValues = {
    partnerOrgName: deal?.partner_org_name ?? defaultOrgName,
    clientDomain: deal?.client_domain ?? "",
  };

  function handleAssignNext() {
    // Gate: a certified BDM/PO must be chosen (the picker only lets certified
    // candidates be selected, so a non-null id is sufficient) — CHP-368.
    if (!pickedOwnerId) {
      setAssignError("Select who to assign this deal to.");
      return;
    }
    setAssignError(null);
    setStep((s) => Math.min(s + 1, LAST_STEP));
  }

  function handleClientDetailsComplete(created: Deal) {
    setDealId(created.id);
    setDeal(created);
    setCrossStepError(null);
    setStep((s) => Math.min(s + 1, LAST_STEP));
  }

  function handleStepComplete(updated: Deal) {
    setDeal(updated);
    setCrossStepError(null);
    setStep((s) => Math.min(s + 1, LAST_STEP));
  }

  function handleBack() {
    setCrossStepError(null);
    setStep((s) => Math.max(s - 1, 1));
  }

  // CHP-474: a submit on the review step failed validation on a field owned by
  // an earlier step. Map field -> step kind -> step index and navigate there,
  // seeding the offending field so the destination step highlights it.
  function handleSubmitFieldError(field: string) {
    const kind = stepForField(field);
    if (!kind) return;
    const targetIndex = steps.findIndex((s) => s.kind === kind);
    if (targetIndex === -1) return;
    setCrossStepError({ kind, field });
    setStep(targetIndex + 1);
  }

  function handleSubmitted() {
    setSubmitted(true);
  }

  function handleStartOver() {
    setStep(1);
    setDealId(null);
    setDeal(null);
    setSubmitted(false);
    setPickedOwnerId(null);
    setPickedOwnerName(null);
    setAssignError(null);
    setCrossStepError(null);
  }

  const currentStep = steps[step - 1];
  const currentKind = currentStep?.kind;

  // Move focus to the active step's region on each change so keyboard and
  // screen-reader users land on the new content (skip the initial mount so
  // we don't steal focus on first render).
  const stepRegionRef = React.useRef<HTMLDivElement>(null);
  const didMountRef = React.useRef(false);
  React.useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    stepRegionRef.current?.focus();
  }, [step]);

  if (submitted && deal) {
    return <DealConfirmation deal={deal} onStartOver={handleStartOver} />;
  }

  return (
    <div className="container py-6">
      <header className="mb-6">
        {backTo ? (
          <Link
            to={backTo}
            className="mb-2 inline-flex items-center gap-1 text-sm text-muted-foreground hover:underline"
            data-testid="wizard-back-to-deals"
            // CHP-974: exiting with an unsubmitted draft confirms nothing is
            // lost. dealId is only set once a step has saved; the header (and
            // this link) never renders post-submission, so no submitted check.
            onClick={() => {
              if (dealId) toast(dealToasts.draftSaved());
            }}
          >
            <ArrowLeft className="h-4 w-4" aria-hidden /> Back to deals
          </Link>
        ) : null}
        <h1 className="text-2xl font-semibold tracking-tight">Register new deal</h1>
        <p className="text-sm text-muted-foreground">
          Submit the opportunity details below to start your registration.
        </p>
        {targetOwner ? (
          <p className="mt-1 text-sm font-medium">
            Registering on behalf of {targetOwner.name ?? "the selected partner"}
          </p>
        ) : null}
        {/* CHP-285: resuming a saved draft shows when it was last saved, so the
            user knows which draft they picked up. initialDeal is only passed on
            the ?draftId resume path. */}
        {initialDeal ? (
          <InlineAlert className="mt-3">
            <span data-testid="draft-resume-banner">
              Resuming your saved draft — last saved{" "}
              {new Date(initialDeal.updated_at).toLocaleString("en-AU", {
                dateStyle: "medium",
                timeStyle: "short",
              })}
              .
            </span>
          </InlineAlert>
        ) : null}
      </header>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Progress bar — its own row in the form column so its width tracks
            the active form area, not the full page. */}
        <div className="lg:col-span-2">
          <WizardStepper step={step} labels={steps.map((s) => s.label)} />
        </div>

        {/* Form body — second row of the form column. The side cards share
            this row so their tops align with the form, not the progress bar. */}
        <div className="lg:col-span-2 lg:col-start-1 lg:row-start-2">
          {/* "Step N of M" announcer for assistive tech. sr-only is absolutely
              positioned, so it must NOT drive layout spacing (a space-y here
              would push the form body down by one gap with nothing above it). */}
          <p className="sr-only" aria-live="polite">
            Step {step} of {LAST_STEP}: {currentStep?.label}
          </p>

          {/* Step content */}
          <div ref={stepRegionRef} tabIndex={-1} className="outline-none">
            {currentKind === "assign" && accessToken && (
              <Card>
                <CardHeader>
                  <CardTitle>Step 1: Assign Deal Owner</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4">
                    <DealAssigneeSelector
                      accessToken={accessToken}
                      value={pickedOwnerId}
                      onChange={(id, name) => {
                        setPickedOwnerId(id);
                        setPickedOwnerName(name);
                        setAssignError(null);
                      }}
                    />
                    {assignError ? <FormError>{assignError}</FormError> : null}
                    <div className="flex justify-end">
                      <Button type="button" onClick={handleAssignNext}>
                        Next
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            {currentKind === "client" && (
              <DealWizardStep1
                deal={deal}
                identity={identityValues}
                targetBdmId={effectiveTargetBdmId}
                stepNumber={managerOnBehalf ? 2 : 1}
                initialErrorField={crossStepError?.kind === "client" ? crossStepError.field : null}
                onComplete={handleClientDetailsComplete}
                onCancel={onCancel ? () => setConfirmCancel(true) : undefined}
              />
            )}
            {currentKind === "opportunity" && deal && (
              <DealWizardStep2
                deal={deal}
                onComplete={handleStepComplete}
                onBack={handleBack}
                stepNumber={managerOnBehalf ? 3 : 2}
                initialErrorField={
                  crossStepError?.kind === "opportunity" ? crossStepError.field : null
                }
              />
            )}
            {currentKind === "review" && deal && dealId && (
              <DealWizardStep3
                deal={deal}
                onSubmit={handleSubmitted}
                onBack={handleBack}
                onDealChange={setDeal}
                dealId={dealId}
                stepNumber={managerOnBehalf ? 4 : 3}
                onFieldError={handleSubmitFieldError}
                // On-behalf (CHP-202): surface a Partner & Owner Identity block
                // so the actor verifies who the deal is registered to before
                // submit. Partner self-registration leaves this undefined.
                ownerIdentity={
                  targetOwner || managerOnBehalf
                    ? {
                        orgName: targetOwner?.orgName ?? deal.partner_org_name ?? null,
                        ownerName: targetOwner?.name ?? pickedOwnerName,
                      }
                    : undefined
                }
              />
            )}
          </div>
        </div>

        {/* Side cards — aligned to the top of the form body (row 2), present
            on every step. */}
        <aside className="space-y-4 self-start lg:col-start-3 lg:row-start-2 lg:sticky lg:top-24">
          {/* CHP-809: the side-panel narrative must match the actual flow. A
              CM/RA/RM on-behalf registration IS the approval — the deal lands
              LIVE in the assigned PARTNER's portfolio immediately (no 24h
              review, ownership/expiry framed around the assignee, not "you").
              Partner self-registration keeps the review-queue copy. Mirrors the
              DealConfirmation branch (CHP-368). */}
          {onBehalf ? (
            <>
              <Card>
                {/* Icon stacked above the headline, everything left-justified
                    (CHP-212). Headline is uppercased via CSS so the markup text
                    stays title-case for screen readers (RACCPE-17). */}
                <CardHeader className="space-y-1">
                  <Clock className="h-5 w-5 text-primary" aria-hidden />
                  <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                    Goes Live Immediately
                  </p>
                  <CardTitle className="text-sm font-semibold text-muted-foreground">
                    No Review Queue
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Registering on a partner's behalf is the approval — the deal lands live in the
                    assigned partner's portfolio on submission, with no 24-hour review.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="space-y-1">
                  <ShieldCheck className="h-5 w-5 text-primary" aria-hidden />
                  <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                    Deal Protection
                  </p>
                  <CardTitle className="text-sm font-semibold text-muted-foreground">
                    90-Day Exclusivity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    The opportunity is locked to the assigned partner for 90 days from registration.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="space-y-1">
                  <Bell className="h-5 w-5 text-primary" aria-hidden />
                  <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                    Registration Expiry
                  </p>
                  <CardTitle className="text-sm font-semibold text-muted-foreground">
                    14-Day Advance Notice
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    The assigned partner is notified 14 days before expiry and can request an
                    extension if they need more time to close.
                  </p>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              <Card>
                {/* Icon stacked above the headline, everything left-justified
                    (CHP-212). Headline is uppercased via CSS so the markup text
                    stays title-case for screen readers (RACCPE-17). */}
                <CardHeader className="space-y-1">
                  <Clock className="h-5 w-5 text-primary" aria-hidden />
                  <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                    Turnaround
                  </p>
                  <CardTitle className="text-sm font-semibold text-muted-foreground">
                    24-Hour Response
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Expect an answer regarding your registration within 24 hours of submission.
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="space-y-1">
                  <ShieldCheck className="h-5 w-5 text-primary" aria-hidden />
                  <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                    Deal Protection
                  </p>
                  <CardTitle className="text-sm font-semibold text-muted-foreground">
                    90-Day Exclusivity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Once approved, this opportunity is locked to you for 90 days.
                  </p>
                </CardContent>
              </Card>

              {/* CHP-344: static informational card — registration expiry notice. */}
              <Card>
                <CardHeader className="space-y-1">
                  <Bell className="h-5 w-5 text-primary" aria-hidden />
                  <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                    Registration Expiry
                  </p>
                  <CardTitle className="text-sm font-semibold text-muted-foreground">
                    14-Day Advance Notice
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    We'll notify you 14 days before expiry. Request an extension if you require
                    additional time to close your opportunity.
                  </p>
                </CardContent>
              </Card>
            </>
          )}
        </aside>
      </div>

      {/* CHP-1014: Step-1 Cancel confirmation. House ConfirmDialog per the
          ticket ("same style as other confirmation screens") — "Keep editing"
          is the safe first-focus action; discard navigates via the route. */}
      {onCancel ? (
        <ConfirmDialog
          open={confirmCancel}
          onOpenChange={setConfirmCancel}
          title="Discard this deal registration?"
          description="You'll be returned to the deals dashboard."
          variant="destructive"
          confirmLabel="Discard registration"
          cancelLabel="Keep editing"
          onConfirm={() => {
            setConfirmCancel(false);
            onCancel();
          }}
        />
      ) : null}
    </div>
  );
}

function WizardStepper({ step, labels }: { step: number; labels: string[] }) {
  return (
    <nav aria-label="Wizard progress" className="rounded-lg border bg-card p-3">
      <ol className="flex items-center">
        {labels.map((label, i) => {
          const stepNum = i + 1;
          const isCurrent = stepNum === step;
          const isCompleted = stepNum < step;
          return (
            <React.Fragment key={label}>
              {i > 0 && <li aria-hidden className="mx-2 h-px flex-1 bg-border" />}
              <li className="flex items-center gap-2" aria-current={isCurrent ? "step" : undefined}>
                <StepCircle
                  n={stepNum}
                  state={isCompleted ? "done" : isCurrent ? "current" : "upcoming"}
                />
                <span
                  className={cn(
                    "hidden text-sm sm:inline",
                    isCurrent ? "font-semibold text-foreground" : "text-muted-foreground",
                  )}
                >
                  {label}
                </span>
              </li>
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
